import { ethers } from 'ethers';
import {
  waitForOnChainTransaction,
  worldChainProvider,
} from './blockchain';
import {
  getPermit2ApprovalExpiration,
  MAX_UINT160,
  PERMIT2_ALLOWANCE_ADDRESS,
  PERMIT2_ALLOWANCE_SETTLEMENT_ADDRESS,
} from './permit2';
import { getMetaMaskSigner } from './metamask';
import {
  getWorldMiniKitErrorMessage,
  sendWorldTransaction,
  type WorldCalldataTransaction,
  type WorldTransactionNotice,
} from './worldMiniKit';

export type SendTokenRequest = {
  tokenAddress: string;
  recipientAddress: string;
  amount: string;
  decimals: number;
  rawBalance: bigint;
  walletAddress: string;
  transactionNotice?: WorldTransactionNotice;
};

export type SendTokenMetaMaskRequest = SendTokenRequest;

export type SendTokenResult = {
  txHash: string;
  userOpHash: string | null;
  approvalTxHashes: string[];
  amountBaseUnits: string;
};

const ERC20_ALLOWANCE_ABI = [
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
] as const;

const PERMIT2_ALLOWANCE_ABI = [
  'function allowance(address owner, address token, address spender) view returns (uint160 amount, uint48 expiration, uint48 nonce)',
  'function approve(address token, address spender, uint160 amount, uint48 expiration)',
] as const;

const PERMIT2_ALLOWANCE_SETTLEMENT_ABI = [
  'function transferFromAllowance(address token, address recipient, uint256 amount)',
] as const;

const ERC20_ALLOWANCE_INTERFACE = new ethers.Interface(ERC20_ALLOWANCE_ABI);
const PERMIT2_ALLOWANCE_INTERFACE = new ethers.Interface(PERMIT2_ALLOWANCE_ABI);
const PERMIT2_ALLOWANCE_SETTLEMENT_INTERFACE = new ethers.Interface(PERMIT2_ALLOWANCE_SETTLEMENT_ABI);

export type Permit2AllowanceState = {
  amount: bigint;
  expiration: bigint;
};

export type Permit2ApprovalStep = {
  kind: 'erc20' | 'permit2';
  transaction: WorldCalldataTransaction;
};

export type Permit2AllowanceTransferPlan = {
  amountInBaseUnits: bigint;
  permit2ApprovalExpiration: bigint;
  approvalSteps: Permit2ApprovalStep[];
  transferTransaction: WorldCalldataTransaction;
};

export type BuildPermit2AllowanceTransferPlanRequest = {
  tokenAddress: string;
  recipientAddress: string;
  amountInBaseUnits: bigint;
  erc20Allowance: bigint;
  permit2Allowance: Permit2AllowanceState;
  nowSeconds: bigint;
  permit2ApprovalExpiration?: bigint;
};

function parseAmount(amount: string, decimals: number, rawBalance: bigint): bigint {
  let amountInBaseUnits: bigint;
  try {
    amountInBaseUnits = ethers.parseUnits(amount.trim(), decimals);
  } catch {
    throw new Error(`傳送數量最多支援 ${decimals} 位小數。`);
  }

  if (amountInBaseUnits <= 0n) throw new Error('傳送數量必須大於 0。');
  if (amountInBaseUnits > rawBalance) throw new Error('傳送數量不可超過目前鏈上餘額。');
  if (amountInBaseUnits > MAX_UINT160) throw new Error('傳送數量超過 Permit2 支援上限。');
  return amountInBaseUnits;
}

export function buildPermit2AllowanceTransferPlan({
  tokenAddress,
  recipientAddress,
  amountInBaseUnits,
  erc20Allowance,
  permit2Allowance,
  nowSeconds,
  permit2ApprovalExpiration: requestedPermit2ApprovalExpiration,
}: BuildPermit2AllowanceTransferPlanRequest): Permit2AllowanceTransferPlan {
  if (!ethers.isAddress(tokenAddress) || !ethers.isAddress(recipientAddress)) {
    throw new Error('請輸入有效的錢包地址。');
  }
  if (amountInBaseUnits <= 0n) throw new Error('傳送數量必須大於 0。');
  if (amountInBaseUnits > MAX_UINT160) throw new Error('傳送數量超過 Permit2 支援上限。');

  const approvalSteps: Permit2ApprovalStep[] = [];
  const permit2ApprovalExpiration = requestedPermit2ApprovalExpiration
    ?? getPermit2ApprovalExpiration(nowSeconds);

  if (erc20Allowance < amountInBaseUnits) {
    approvalSteps.push({
      kind: 'erc20',
      transaction: {
        to: tokenAddress,
        data: ERC20_ALLOWANCE_INTERFACE.encodeFunctionData('approve', [
          PERMIT2_ALLOWANCE_ADDRESS,
          amountInBaseUnits,
        ]),
      },
    });
  }

  if (
    permit2Allowance.amount < amountInBaseUnits
    || permit2Allowance.expiration <= nowSeconds
  ) {
    approvalSteps.push({
      kind: 'permit2',
      transaction: {
        to: PERMIT2_ALLOWANCE_ADDRESS,
        data: PERMIT2_ALLOWANCE_INTERFACE.encodeFunctionData('approve', [
          tokenAddress,
          PERMIT2_ALLOWANCE_SETTLEMENT_ADDRESS,
          amountInBaseUnits,
          permit2ApprovalExpiration,
        ]),
      },
    });
  }

  return {
    amountInBaseUnits,
    permit2ApprovalExpiration,
    approvalSteps,
    transferTransaction: {
      to: PERMIT2_ALLOWANCE_SETTLEMENT_ADDRESS,
      data: PERMIT2_ALLOWANCE_SETTLEMENT_INTERFACE.encodeFunctionData('transferFromAllowance', [
        tokenAddress,
        recipientAddress,
        amountInBaseUnits,
      ]),
    },
  };
}

async function readPermit2Allowance(
  owner: string,
  tokenAddress: string,
  provider: ethers.Provider,
): Promise<Permit2AllowanceState> {
  const permit2 = new ethers.Contract(PERMIT2_ALLOWANCE_ADDRESS, PERMIT2_ALLOWANCE_ABI, provider);
  const result = await permit2.allowance(
    owner,
    tokenAddress,
    PERMIT2_ALLOWANCE_SETTLEMENT_ADDRESS,
  ) as [bigint, bigint, bigint];

  return { amount: result[0], expiration: result[1] };
}

export async function buildPermit2AllowanceTransferPlanForOwner({
  owner,
  tokenAddress,
  recipientAddress,
  amountInBaseUnits,
  provider,
  permit2ApprovalExpiration,
}: {
  owner: string;
  tokenAddress: string;
  recipientAddress: string;
  amountInBaseUnits: bigint;
  provider: ethers.Provider;
  permit2ApprovalExpiration?: bigint;
}): Promise<Permit2AllowanceTransferPlan> {
  const token = new ethers.Contract(tokenAddress, ERC20_ALLOWANCE_ABI, provider);
  const [erc20Allowance, permit2Allowance] = await Promise.all([
    token.allowance(owner, PERMIT2_ALLOWANCE_ADDRESS) as Promise<bigint>,
    readPermit2Allowance(owner, tokenAddress, provider),
  ]);

  return buildPermit2AllowanceTransferPlan({
    tokenAddress,
    recipientAddress,
    amountInBaseUnits,
    erc20Allowance,
    permit2Allowance,
    nowSeconds: BigInt(Math.floor(Date.now() / 1000)),
    permit2ApprovalExpiration,
  });
}

export async function sendTokenWithWorldApp({
  tokenAddress,
  recipientAddress,
  amount,
  decimals,
  rawBalance,
  walletAddress,
  transactionNotice,
}: SendTokenRequest): Promise<SendTokenResult> {
  if (!ethers.isAddress(tokenAddress) || !ethers.isAddress(recipientAddress) || !ethers.isAddress(walletAddress)) {
    throw new Error('請輸入有效的錢包地址。');
  }

  const amountInBaseUnits = parseAmount(amount, decimals, rawBalance);

  try {
    const plan = await buildPermit2AllowanceTransferPlanForOwner({
      owner: walletAddress,
      tokenAddress,
      recipientAddress,
      amountInBaseUnits,
      provider: worldChainProvider,
      permit2ApprovalExpiration: 0n,
    });
    const { userOpHash, txHash } = await sendWorldTransaction([
      ...plan.approvalSteps.map((step) => step.transaction),
      plan.transferTransaction,
    ], undefined, transactionNotice);
    return {
      txHash,
      userOpHash,
      approvalTxHashes: [],
      amountBaseUnits: amountInBaseUnits.toString(),
    };
  } catch (error) {
    throw new Error(getWorldMiniKitErrorMessage(error));
  }
}

export async function sendTokenWithMetaMask({
  tokenAddress,
  recipientAddress,
  amount,
  decimals,
  rawBalance,
  walletAddress,
}: SendTokenMetaMaskRequest): Promise<SendTokenResult> {
  if (!ethers.isAddress(tokenAddress) || !ethers.isAddress(recipientAddress) || !ethers.isAddress(walletAddress)) {
    throw new Error('請輸入有效的錢包地址。');
  }

  const amountInBaseUnits = parseAmount(amount, decimals, rawBalance);
  const { address: signerAddress, provider, signer } = await getMetaMaskSigner();
  if (signerAddress.toLowerCase() !== walletAddress.toLowerCase()) {
    throw new Error('MetaMask 帳戶已變更，請重新選擇 MetaMask 分頁並更新餘額。');
  }

  const plan = await buildPermit2AllowanceTransferPlanForOwner({
    owner: signerAddress,
    tokenAddress,
    recipientAddress,
    amountInBaseUnits,
    provider,
  });

  const approvalTxHashes: string[] = [];
  for (const step of plan.approvalSteps) {
    const transaction = await signer.sendTransaction(step.transaction);
    approvalTxHashes.push(transaction.hash);
    const receipt = await transaction.wait();
    if (!receipt || receipt.status !== 1) {
      throw new Error(step.kind === 'erc20'
        ? 'ERC-20 授權交易未成功。'
        : 'Permit2 授權交易未成功。');
    }
  }

  const transaction = await signer.sendTransaction(plan.transferTransaction);
  return {
    txHash: transaction.hash,
    userOpHash: null,
    approvalTxHashes,
    amountBaseUnits: amountInBaseUnits.toString(),
  };
}

export { waitForOnChainTransaction };
